import {format} from 'date-fns'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {
  DataObjectId,
  ObjectType,
  UserEventsByDay,
  UserEventsByTime
} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {Sync} from '@client/sync'
import {getUserProxyByUserId} from '@components/common/base/avatar/use-user-proxy'
import {ErrorBoundary} from '@components/common/error-boundary/error-boundary'
import {dateFormat} from '../stats-timeline-store'
import {StatisticsTile} from './tile'

const TwLabel = tw.div`
  py-3
  h-2/3
  sticky
  bg-obscure-constant-old
  text-14
  font-semibold
  text-bg-main-old
  rounded-l-4
  top-16
  text-center
  w-88
  leading-5
  -mr-2
`

const TwLine = tw.div`
  min-w-4
  bg-obscure-constant-old
  rounded
`

interface Props {
  date: string
  restore: (objId: DataObjectId, objType: ObjectType) => void
  errors: Set<number>
  reportError: (timestamp: number) => void
  eventsByDay: UserEventsByDay
}

interface PropsTiles extends Props {
  events: UserEventsByTime
}

const Tiles = (props: PropsTiles) => {
  const {events, restore, errors, reportError} = props
  const box = Sync.getCurrentSandbox()
  const hierarchy = DepartmentsClient.getHierarchy(box)
  return (
    <>
      {Object.keys(events)
        .filter(k => !errors.has(+k))
        .map(k => {
          const onError = () => {
            logger.warn('Error with event item', events[+k])
            reportError(+k)
          }
          return (
            <ErrorBoundary key={k} silent={true} onError={onError}>
              <StatisticsTile
                user={getUserProxyByUserId(events[+k].user)}
                event={events[+k]}
                time={+k}
                restore={restore}
                hasError={errors.has(+k)}
                hierarchy={hierarchy}
              />
            </ErrorBoundary>
          )
        })}
    </>
  )
}

export const StatisticsTiles = (props: Props) => {
  const {eventsByDay, date} = props

  return (
    <div className="flex mt-16 last:mb-24">
      <TwLabel>{getDate(date)}</TwLabel>
      <TwLine />
      <div className="w-full ml-8 max-w-640">
        <Tiles {...props} events={eventsByDay[date]} />
      </div>
    </div>
  )
}

export const getDate = (date: string) => {
  const dateSplit = date.split('.')
  const day = dateSplit[0]
  const month = +dateSplit[1] - 1
  const year = dateSplit[2]
  const today = new Date()
  const yesterday = new Date().setDate(new Date().getDate() - 1)
  const currentYear = new Date().getFullYear().toString()

  if (format(today, dateFormat) === date) {
    return Lang.msg.common.today
  } else if (format(yesterday, dateFormat) === date) {
    return Lang.msg.yesterday
  } else if (currentYear !== year) {
    return date
  } else {
    return `${day} ${Lang.msg.statsTimeline.monthNames[month]}`
  }
}
